import { useState } from 'react';
import { Badge, Button, Form, Offcanvas } from 'react-bootstrap';
import PriceRangeFilter from './PriceRangeFilter';
import { VERTICALS, VERTICAL_LABEL } from '../wording';

// Floating filter panel — right-side Offcanvas on < lg (trigger with active-count badge), inline
// panel on >= lg. Mirrors the browse_filters_and_mobile_panel.md MobileFilterPanel pattern.
//
//  fields        which filters to render, e.g. ['status','vertical','date','amount','overdue','hasReminders']
//  statusOptions [{ value, label }] for the status <select> (page-specific enum)
//  q             the useFinanceQuery instance (get / setFilter / activeFilterCount / clearFilters)

function FilterFields({ fields, statusOptions, q }) {
  return (
    <div className="d-flex flex-column gap-3">
      {fields.includes('status') && statusOptions?.length > 0 && (
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Status</Form.Label>
          <Form.Select size="sm" value={q.get('status')} onChange={(e) => q.setFilter('status', e.target.value)}>
            <option value="">Any status</option>
            {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Form.Select>
        </Form.Group>
      )}

      {fields.includes('vertical') && (
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Vertical</Form.Label>
          {VERTICALS.map((v) => {
            const selected = q.get('vertical').split(',').filter(Boolean);
            const checked = selected.includes(v);
            return (
              <Form.Check
                key={v}
                type="checkbox"
                id={`fin-filter-vertical-${v}`}
                label={VERTICAL_LABEL[v]}
                checked={checked}
                onChange={() => {
                  const next = checked ? selected.filter((x) => x !== v) : [...selected, v];
                  q.setFilter('vertical', next.join(','));
                }}
              />
            );
          })}
        </Form.Group>
      )}

      {fields.includes('date') && (
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Date range</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control size="sm" type="date" value={q.get('dateFrom')} onChange={(e) => q.setFilter('dateFrom', e.target.value)} />
            <Form.Control size="sm" type="date" value={q.get('dateTo')} onChange={(e) => q.setFilter('dateTo', e.target.value)} />
          </div>
        </Form.Group>
      )}

      {fields.includes('amount') && (
        <Form.Group>
          <PriceRangeFilter
            value={{ min: q.get('minAmount') || null, max: q.get('maxAmount') || null }}
            ceiling={200000}
            onChange={({ min, max }) => {
              q.setFilter('minAmount', min == null ? '' : Math.round(min));
              q.setFilter('maxAmount', max == null ? '' : Math.round(max));
            }}
          />
        </Form.Group>
      )}

      {fields.includes('overdue') && (
        <Form.Check
          type="switch"
          id="fin-filter-overdue"
          label="Overdue only"
          checked={q.get('overdue') === '1'}
          onChange={(e) => q.setFilter('overdue', e.target.checked ? '1' : '')}
        />
      )}

      {fields.includes('hasReminders') && (
        <Form.Check
          type="switch"
          id="fin-filter-has-reminders"
          label="Has reminders sent"
          checked={q.get('hasReminders') === '1'}
          onChange={(e) => q.setFilter('hasReminders', e.target.checked ? '1' : '')}
        />
      )}

      {fields.includes('owner') && (
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Owner</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Name, email or id"
            value={q.get('ownerId')}
            onChange={(e) => q.setFilter('ownerId', e.target.value)}
          />
        </Form.Group>
      )}

      {fields.includes('method') && (
        <Form.Group>
          <Form.Label className="small text-muted mb-1">Payment method</Form.Label>
          <Form.Control
            size="sm"
            placeholder="UPI, cash, card…"
            value={q.get('method')}
            onChange={(e) => q.setFilter('method', e.target.value)}
          />
        </Form.Group>
      )}

      {q.activeFilterCount > 0 && (
        <Button size="sm" variant="outline-secondary" onClick={q.clearFilters}>
          Clear all filters
        </Button>
      )}
    </div>
  );
}

export default function FinanceFilterPanel({ fields = [], statusOptions = [], q }) {
  const [open, setOpen] = useState(false);
  if (!fields.length) return null;

  return (
    <>
      {/* < lg: trigger + Offcanvas */}
      <Button
        variant="outline-secondary"
        size="sm"
        className="d-lg-none"
        onClick={() => setOpen(true)}
      >
        Filters
        {q.activeFilterCount > 0 && <Badge bg="primary" className="ms-2">{q.activeFilterCount}</Badge>}
      </Button>
      <Offcanvas show={open} onHide={() => setOpen(false)} placement="end" className="d-lg-none">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filters</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <FilterFields fields={fields} statusOptions={statusOptions} q={q} />
          <Button className="w-100 mt-3" onClick={() => setOpen(false)}>Done</Button>
        </Offcanvas.Body>
      </Offcanvas>

      {/* >= lg: inline card */}
      <div className="d-none d-lg-block border rounded p-3 bg-light finance-filter-inline" style={{ minWidth: 240, maxWidth: 280 }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <strong className="small">Filters</strong>
          {q.activeFilterCount > 0 && <Badge bg="primary">{q.activeFilterCount}</Badge>}
        </div>
        <FilterFields fields={fields} statusOptions={statusOptions} q={q} />
      </div>
    </>
  );
}
